import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import FocusSkeleton from './FocusSkeleton';
import ErrorState from '../ErrorState/ErrorState';
import { useCachedFetch } from './../../hooks/useCachedFetch';
import { AnimatedOnScroll } from '../AnimatedOnScroll';
import { IMAGES_BASE_URL } from '../../config/constants';
import './Focus.scss';

const Focus = ({ page }) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;

  const { 
    data: focusData, 
    loading: dataLoading, 
    error: dataError, 
    refetch
  } = useCachedFetch('/data/locales/focus.json');

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (dataLoading) return;

    if (dataError) {
      setHasError(true);
      setIsLoading(false);
      return;
    }

    const arrowImage = focusData?.focusData?.arrowImage;
    const data = focusData?.focusData?.[page];
    
    if (data && 
        data.quoteTitle && 
        data.quoteText && 
        data.services && 
        Array.isArray(data.services) && 
        data.services.length > 0 && 
        arrowImage) {
      setHasError(false);
    } else {
      setHasError(true);
    }

    setIsLoading(false);
  }, [dataLoading, dataError, focusData, page]);

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  const focusDataState = useMemo(() => {
    if (!focusData) return null;

    const arrowImage = focusData.focusData?.arrowImage;
    const data = focusData.focusData?.[page];
    
    if (data && 
        data.quoteTitle && 
        data.quoteText && 
        data.services && 
        Array.isArray(data.services) && 
        data.services.length > 0 && 
        arrowImage) {
      return { data, arrow: arrowImage };
    }
    
    return null;
  }, [focusData, page]);

  const getTranslatedText = useCallback((textData) => {
    if (typeof textData === 'object') {
      if (textData[currentLang] !== undefined) {
        return textData[currentLang];
      }
      if (textData.en !== undefined) {
        return textData.en;
      }
      const availableLang = Object.keys(textData)[0];
      return textData[availableLang];
    }
    return textData;
  }, [currentLang]);

  const translatedQuoteTitle = useMemo(() => 
    focusDataState ? getTranslatedText(focusDataState.data.quoteTitle) : '',
    [focusDataState, getTranslatedText]
  );

  const translatedQuoteText = useMemo(() => 
    focusDataState ? getTranslatedText(focusDataState.data.quoteText) : '',
    [focusDataState, getTranslatedText]
  );

  const translatedServices = useMemo(() => {
    if (!focusDataState) return [];
    
    return focusDataState.data.services.map(service => ({
      ...service,
      translatedTitle: getTranslatedText(service.title),
      translatedDescription: getTranslatedText(service.description),
      altText: getTranslatedText(service.title)
    }));
  }, [focusDataState, getTranslatedText]);

  if (isLoading) {
    return <FocusSkeleton />;
  }

  if (hasError || !focusDataState) {
    return <ErrorState onRetry={handleRetry} />;
  }

  const { arrow: currentArrow } = focusDataState;

  return (
    <div className="focus__section">
      <div className="design__arrow">
        <div className="arrow__wrapper"></div>
        <img src={`${IMAGES_BASE_URL}${currentArrow}`} alt="Arrow-Focus" className="arrow__down" loading="lazy"/>
      </div>
      <div className="focus__section__wrapper">
        <div className="focus__section__item__desc">
          <div className="focus__section__item__desc__quote">
            {Array.isArray(translatedQuoteTitle) ? (
              translatedQuoteTitle.map((line, index) => (
                <AnimatedOnScroll animation="fade-up" delay={0} key={index}>
                <h3 key={index} className="focus__section__item__desc__quote__title">
                  {line}
                </h3>
                </AnimatedOnScroll>
              ))
            ) : (
              <h3 className="focus__section__item__desc__quote__title">
                {translatedQuoteTitle}
              </h3>
            )}
            <span className="focus__section__item__desc__quote__line" />
          </div>
          <div className="focus__section__item__desc__article">
            <AnimatedOnScroll animation="fade-up" delay={0}>
            <p className="focus__section__item__desc__article__text">
              {translatedQuoteText}
            </p>
            </AnimatedOnScroll>
          </div>
        </div>
        <div className="focus__section__item__perks">
          <div className="right__side__focus__services__section">
            {translatedServices.map((service) => {
              const altText = Array.isArray(service.altText) 
                ? service.altText.join(' ') 
                : service.altText;

              return (
                <div key={service.id} className="services__focus__links__item">
                  <AnimatedOnScroll animation="fade-up" delay={100}>
                  <img src={`${IMAGES_BASE_URL}${service.icon}`} alt={altText} className="focus__icon" loading="lazy"/>

                  {Array.isArray(service.translatedTitle) ? (
                    service.translatedTitle.map((line, index) => (
                      <h3 key={index} className="focus__link__item">
                        {line}
                      </h3>
                    ))
                  ) : (
                    <h3 className="focus__link__item">
                      {service.translatedTitle}
                    </h3>
                  )}

                  <p className="services__focus__link__text">{service.translatedDescription}</p>
                  </AnimatedOnScroll>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(Focus);